import { Mail, Shield, FileText } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  
  // Determine if we're in the advisor section based on URL or user role
  const isAdvisorPath = location.pathname.startsWith('/advisor');
  
  // Also check if logged-in user is an advisor (for shared pages like /calculators, /account-settings)
  let isAdvisorUser = false;
  try {
    isAdvisorUser = localStorage.getItem('userRole') === 'advisor';
  } catch {}
  
  const isAdvisorSection = isAdvisorPath || isAdvisorUser;

  // Consumer app links
  const consumerLinks = [
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'My Finances', path: '/transactions' },
    { name: 'Goals', path: '/goals' },
    { name: 'Contact Us', path: 'mailto:support@finaigpt.com', isExternal: true },
  ];

  // Advisor app links
  const advisorLinks = [
    { name: 'Dashboard', path: '/advisor/dashboard' },
    { name: 'My Clients', path: '/advisor/clients' },
    { name: 'Pricing', path: '/advisor/pricing' },
    { name: 'Contact Us', path: 'mailto:support@finaigpt.com', isExternal: true },
  ];

  const quickLinks = isAdvisorSection ? advisorLinks : consumerLinks;

  return (
    <footer data-testid="main-footer" className="mt-16 relative">
      <div className="h-[1px] bg-gradient-to-r from-transparent via-teal-500/30 to-transparent" />
      <div className="bg-gray-50/50 dark:bg-gray-900/50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-md shadow-teal-500/25">
                <span className="text-white font-bold text-lg">FA</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">FinAI</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isAdvisorSection ? 'Professional Financial Planning' : 'Your Smart Money Manager'}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md leading-relaxed">
              {isAdvisorSection 
                ? 'Create professional financial plans for your clients with powerful tools and beautiful reports.'
                : 'Take control of your finances with AI-powered insights, comprehensive tracking, and personalized financial planning.'
              }
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.isExternal ? (
                    <a 
                      href={link.path} 
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link 
                      to={link.path} 
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">Legal</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-sm text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors flex items-center">
                  <Shield className="w-3 h-3 mr-2" />
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors flex items-center">
                  <FileText className="w-3 h-3 mr-2" />
                  Terms of Service
                </Link>
              </li>
              <li>
                <a href="mailto:support@finaigpt.com" className="text-sm text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors flex items-center">
                  <Mail className="w-3 h-3 mr-2" />
                  Support
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-gray-200/60 dark:border-gray-700/40 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-xs text-gray-500 dark:text-gray-500">
            &copy; {currentYear} FinAI. All rights reserved.
          </p>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
            <span>Made with</span>
            <span className="text-red-500 text-sm">&hearts;</span>
            <span>in India</span>
          </div>
        </div>
      </div>
      </div>
    </footer>
  );
}
